import type { WsMessage } from '../types/contact'

export type WsHandler = (msg: WsMessage) => void

export class ContactWebSocket {
  private ws: WebSocket | null = null
  private handlers: WsHandler[] = []
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private stopped = false

  onConnected?: () => void
  onDisconnected?: () => void

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return
    this.stopped = false

    const rawBase: string = import.meta.env.VITE_WS_URL ?? '/ws'
    const wsBase = rawBase.startsWith('/')
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${rawBase}`
      : rawBase
    this.ws = new WebSocket(`${wsBase}/contacts`)

    this.ws.addEventListener('open', () => this.onConnected?.())

    this.ws.addEventListener('message', (evt) => {
      try {
        const msg = JSON.parse(evt.data as string) as WsMessage
        this.handlers.forEach((h) => h(msg))
      } catch {
        // ignore malformed frames
      }
    })

    this.ws.addEventListener('close', () => {
      this.cleanup()
      this.onDisconnected?.()
      if (!this.stopped) {
        this.reconnectTimeout = setTimeout(() => this.connect(), 5_000)
      }
    })

    this.ws.addEventListener('error', () => this.ws?.close())
  }

  disconnect(): void {
    this.stopped = true
    this.cleanup()
    this.ws?.close()
    this.ws = null
  }

  addHandler(handler: WsHandler): () => void {
    this.handlers.push(handler)
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler)
    }
  }

  private cleanup(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }
}
